import mixpanel from 'mixpanel-browser';
import { AnalyticsApi, AnalyticsEvent } from '@backstage/core-plugin-api';
import { getPageSection } from './pageSection';
import { getPageTitle } from './pageTitle';
import {
  getSessionId,
  peekActiveSessionId,
  startActivityTracking,
  touchActivity,
} from './session';

/**
 * Shared ready flag for the analytics pipeline.
 *
 * Set by MixpanelInit once mixpanel.init() has run AND the user has been
 * identified. Until this is true, incoming AnalyticsEvents are buffered
 * (up to BUFFER_LIMIT) so the first navigation after login is not lost
 * and does not ship with a random UUID as distinct_id.
 */
let analyticsReady = false;

export function markAnalyticsReady(): void {
  analyticsReady = true;
  flushBuffer();
}

// Last in-app pathname before the current one. Updated on each navigate event.
// Used by entity page tracking to derive `source` (catalog_list vs direct_link)
// in an SPA, where document.referrer reflects the external referrer only.
let lastInAppPath: string = '';

export function getLastInAppPath(): string {
  return lastInAppPath;
}

const BUFFER_LIMIT = 50;
let eventBuffer: Array<() => void> = [];

function queueOrFire(fn: () => void): void {
  if (analyticsReady) {
    fn();
  } else if (eventBuffer.length < BUFFER_LIMIT) {
    eventBuffer.push(fn);
  }
  // Overflow is dropped silently — analytics must never leak memory.
}

function flushBuffer(): void {
  const toFlush = eventBuffer;
  eventBuffer = [];
  for (const fn of toFlush) {
    try {
      fn();
    } catch (_e) {
      // silent
    }
  }
}

// Test-only: reset the ready flag and buffer between tests.
export function __resetAnalyticsForTest(): void {
  analyticsReady = false;
  eventBuffer = [];
  lastInAppPath = '';
}

// The session id is resolved when the event happens (default argument), not
// inside the queued closure: events buffered before identify run later, and
// must keep the session they belonged to.
function safeTrack(
  eventName: string,
  properties: Record<string, any>,
  sessionId: string = getSessionId(),
): void {
  queueOrFire(() => {
    try {
      mixpanel.track(eventName, { ...properties, session_id: sessionId });
    } catch (_e) {
      // silent
    }
  });
}

/**
 * Fire Mixpanel's built-in `$mp_web_page_view` event ourselves, instead of
 * letting init's `track_pageview` auto-tracker do it. This routes the event
 * through the same buffer as our custom events, so — like everything else —
 * it only ships after the user has been identified, never under an anonymous
 * `$device:` id. The `$mp_autocapture: true` marker matches what Mixpanel's
 * own auto-tracker sets, so the built-in web-analytics / DAU / page
 * performance reports treat this event identically to a native pageview.
 *
 * The URL props and page title are pinned at navigation time rather than left
 * to `track_pageview()`'s defaults: buffered events run as deferred closures,
 * so `mixpanel.track_pageview()` reading `window.location` / `document.title`
 * at flush time could attribute a buffered pageview to a later URL or title.
 * `path`, `search`, and `$title` win over the lazily-read defaults; the
 * remaining default props (domain, protocol) are stable enough to read at
 * flush time. `title` is passed in by the caller (the destination page's
 * title) rather than read from `document.title` here, so this can never
 * race against whichever navigation last wrote `document.title`.
 */
function safePageview(path: string, query: string, title: string): void {
  const sessionId = getSessionId();
  queueOrFire(() => {
    try {
      mixpanel.track_pageview({
        $mp_autocapture: true,
        current_url_path: path,
        current_url_search: query ? `?${query}` : '',
        $title: title,
        session_id: sessionId,
      });
    } catch (_e) {
      // silent
    }
  });
}

export class MixpanelAnalyticsApi implements AnalyticsApi {
  private prevPath: string = '';
  private prevSection: string = 'unknown';
  private pageEntryTime: number = Date.now();
  private lastSearchQuery: string = '';
  private pageEntrySessionId: string | undefined = undefined;
  private boundBeforeUnload: () => void;
  private stopActivityTracking: () => void;

  constructor() {
    this.boundBeforeUnload = this.handleBeforeUnload.bind(this);
    window.addEventListener('beforeunload', this.boundBeforeUnload);
    this.stopActivityTracking = startActivityTracking(
      typeof document !== 'undefined' ? document : undefined,
    );
  }

  destroy(): void {
    window.removeEventListener('beforeunload', this.boundBeforeUnload);
    this.stopActivityTracking();
  }

  private handleBeforeUnload(): void {
    if (this.prevPath) {
      // Closing a tab neither rotates nor extends the session, so only peek.
      // A page left open past the timeout, or whose session another tab has
      // rotated, must not stretch the old session or start a new one.
      const active = peekActiveSessionId();
      if (active === undefined || active !== this.pageEntrySessionId) {
        return;
      }
      // beforeunload bypasses the queue — if analytics is not ready by page
      // unload, the event cannot be salvaged anyway.
      try {
        mixpanel.track('page_exited', {
          page_path: this.prevPath,
          page_section: this.prevSection,
          time_on_page_seconds: Math.round(
            (Date.now() - this.pageEntryTime) / 1000,
          ),
          session_id: active,
        });
      } catch (_e) {
        // silent
      }
    }
  }

  captureEvent(event: AnalyticsEvent): void {
    try {
      if (event.action === 'navigate') {
        // Order matters: peek before touch, so page_exited can tell whether
        // the page being left belonged to the session that was still active.
        const activeBefore = peekActiveSessionId();
        const current = touchActivity();
        this.handleNavigate(event, { activeBefore, current });
      } else if (event.action === 'search') {
        touchActivity();
        this.handleSearch(event);
      }
    } catch (_e) {
      // never crash the portal
    }
  }

  private handleNavigate(
    event: AnalyticsEvent,
    session: { activeBefore: string | undefined; current: string },
  ): void {
    const to = (event.attributes?.to as string) || event.subject || '';
    const from = (event.attributes?.from as string) || '';

    // path = just the pathname portion
    const toPath = to.startsWith('http')
      ? new URL(to).pathname
      : to.split('?')[0];
    const toQuery = to.includes('?') ? to.split('?')[1] : '';
    const fromPath = from.startsWith('http')
      ? new URL(from).pathname
      : from.split('?')[0] || this.prevPath;

    const toSection = getPageSection(toPath, toQuery);
    const toTitle = getPageTitle(toPath);

    // Keep document.title in sync with the destination page so the browser
    // tab reflects the route and any later, lazy read of document.title
    // (e.g. track_pageview()'s own defaults) sees a meaningful value.
    try {
      document.title = toTitle;
    } catch (_e) {
      // silent — test environments may not have document
    }

    // fire page_exited for previous page
    if (
      this.prevPath &&
      this.prevPath !== toPath &&
      session.activeBefore !== undefined &&
      session.activeBefore === this.pageEntrySessionId
    ) {
      safeTrack(
        'page_exited',
        {
          page_path: this.prevPath,
          page_section: this.prevSection,
          time_on_page_seconds: Math.round(
            (Date.now() - this.pageEntryTime) / 1000,
          ),
        },
        session.activeBefore,
      );
    }

    // fire page_viewed
    const pageViewedProps: Record<string, any> = {
      page_path: toPath,
      page_title: toTitle,
      page_section: toSection,
      referrer_path: fromPath || '/',
    };

    // entity page detection
    const entityMatch = toPath.match(/^\/catalog\/([^/]+)\/([^/]+)\/([^/]+)/);
    if (entityMatch) {
      pageViewedProps.entity_ref = `${entityMatch[2].toLowerCase()}:${entityMatch[1]}/${entityMatch[3]}`;
      pageViewedProps.entity_kind = entityMatch[2];
    }

    safeTrack('page_viewed', pageViewedProps);

    // Mixpanel's built-in pageview. Fired here (not by init's auto-tracker) so
    // it inherits the post-identify buffer and never lands on an anonymous id.
    safePageview(toPath, toQuery, toTitle);

    // section-specific events
    this.fireSectionEvent(toPath, toSection, fromPath, toQuery);

    // search result click detection
    if (fromPath.startsWith('/search') && this.lastSearchQuery) {
      const resultType = this.getResultTypeFromPath(toPath);
      safeTrack('search_result_clicked', {
        search_query: this.lastSearchQuery,
        result_type: resultType,
        result_title: event.subject || '',
        result_path: toPath,
      });
    }

    // page_load_time is intentionally omitted here:
    // measuring it accurately requires hooking into React's commit phase for
    // each page's first meaningful paint, which this class does not have
    // access to. A naive setTimeout(0) measurement reports ~1–4ms (the
    // macrotask delay), not actual load time, so it would pollute the metric.
    // This event will be added when a per-page performance observer is
    // available.

    // update state
    lastInAppPath = this.prevPath;
    this.prevPath = toPath;
    this.prevSection = toSection;
    this.pageEntryTime = Date.now();
    this.pageEntrySessionId = session.current;
  }

  private fireSectionEvent(
    toPath: string,
    toSection: string,
    fromPath: string,
    toQuery: string = '',
  ): void {
    const isEntityPath = /\/catalog\/[^/]+\/[^/]+\/[^/]+/.test(toPath);

    // catalog_viewed / teams_page_viewed — only for catalog index, not entity
    if (toPath.startsWith('/catalog') && !isEntityPath) {
      if (toSection === 'catalog') {
        safeTrack('catalog_viewed', {});
      } else if (toSection === 'teams') {
        safeTrack('teams_page_viewed', {});
      }
    }

    // catalog_filtered — when catalog URL query params change
    if (
      toPath.startsWith('/catalog') &&
      toQuery &&
      fromPath.startsWith('/catalog') &&
      !isEntityPath
    ) {
      try {
        const params = new URLSearchParams(toQuery);
        const filterEntries: Array<[string, string]> = [];
        params.forEach((value, key) => {
          if (key.startsWith('filters')) {
            filterEntries.push([key, value]);
          }
        });
        if (filterEntries.length > 0) {
          const [firstKey, firstValue] = filterEntries[0];
          const filterType = firstKey.replace(/filters\[?([^\]]+)\]?/, '$1');
          safeTrack('catalog_filtered', {
            filter_type: filterType,
            filter_value: firstValue,
          });
        }
      } catch (_e) {
        // silent
      }
    }

    // team_detail_viewed — group entity page
    const entityMatch = toPath.match(/^\/catalog\/([^/]+)\/([^/]+)\/([^/]+)/);
    if (entityMatch && entityMatch[2].toLowerCase() === 'group') {
      safeTrack('team_detail_viewed', {
        team_name: entityMatch[3],
        team_ref: `group:${entityMatch[1]}/${entityMatch[3]}`,
        source: fromPath.startsWith('/catalog') ? 'teams_list' : 'direct_link',
      });
    }

    // census_card_viewed — the Census tab on a component entity page.
    // Fires on every open, for any component; entity_ref lets us break the
    // count down per component without affecting the total.
    const censusMatch = toPath.match(
      /^\/catalog\/([^/]+)\/component\/([^/]+)\/census\/?$/,
    );
    if (censusMatch) {
      safeTrack('census_card_viewed', {
        entity_ref: `component:${censusMatch[1]}/${censusMatch[2]}`,
        source: fromPath.startsWith('/catalog') ? 'catalog' : 'direct_link',
      });
    }

    // apis_list_viewed
    if (
      toPath.startsWith('/api-docs') &&
      !/\/api-docs\/[^/]+\/api\/[^/]+/.test(toPath)
    ) {
      safeTrack('apis_list_viewed', {});
    }

    // api_detail_viewed
    const apiMatch = toPath.match(/^\/api-docs\/([^/]+)\/api\/([^/]+)/);
    if (apiMatch) {
      safeTrack('api_detail_viewed', {
        api_ref: `api:${apiMatch[1]}/${apiMatch[2]}`,
        source: fromPath.startsWith('/api-docs') ? 'apis_list' : 'direct_link',
      });
    }

    // tech_radar_viewed
    if (toPath.startsWith('/aoe-radar')) {
      safeTrack('tech_radar_viewed', {});
    }

    // docs_page_viewed
    if (toPath.startsWith('/docs')) {
      const docsMatch = toPath.match(/^\/docs\/([^/]+)\/([^/]+)\/([^/]+)/);
      const docProps: Record<string, string> = { doc_path: toPath };
      if (docsMatch) {
        docProps.doc_entity_ref = `${docsMatch[2].toLowerCase()}:${docsMatch[1]}/${docsMatch[3]}`;
      }
      safeTrack('docs_page_viewed', docProps);
    }

    // analytics_page_viewed — derive active_view from the first path
    // segment after `/analytics` (e.g. `/analytics/usage` → 'usage').
    // Bare `/analytics` is reported as 'overview'.
    if (toPath.startsWith('/analytics')) {
      const segments = toPath.split('/').filter(Boolean);
      const activeView = segments[1] || 'overview';
      safeTrack('analytics_page_viewed', { active_view: activeView });
    }

    // architecture_viewed
    if (toPath.startsWith('/architecture')) {
      safeTrack('architecture_viewed', { view_type: 'graph' });
    }

    // security_viewed — derive active_view from the sub-path segment after /security-insights
    if (toPath.startsWith('/security-insights')) {
      const segments = toPath.split('/').filter(Boolean);
      const activeView = segments[1] || 'single_view';
      safeTrack('security_viewed', { active_view: activeView });
    }
  }

  private handleSearch(event: AnalyticsEvent): void {
    const query = event.subject || '';
    const resultsCount = (event.attributes?.resultsCount as number) ?? -1;
    this.lastSearchQuery = query;

    safeTrack('search_executed', {
      search_query: query,
      search_query_length: query.length,
      results_count: resultsCount,
      results_by_type: {},
    });

    if (resultsCount === 0) {
      safeTrack('search_no_results', { search_query: query });
    }
  }

  private getResultTypeFromPath(path: string): string {
    if (path.startsWith('/catalog') && path.includes('/api/')) return 'api';
    if (/\/catalog\/[^/]+\/group\//.test(path)) return 'team';
    if (path.startsWith('/catalog')) return 'component';
    if (path.startsWith('/api-docs')) return 'api';
    if (path.startsWith('/docs')) return 'documentation';
    if (path.startsWith('/ai-marketplace')) return 'ai_plugin';
    return 'component';
  }
}
